/* eslint-disable @typescript-eslint/no-empty-function */
import { Icon } from '@iconify/react';
import { Box, CircularProgress, Divider, IconButton, Stack, Typography, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createAssignment } from '../../services/assignment';
import { ISearchCustomer, IVehicleComponentChange } from '../../types/inventory';
import { TextField, Center, Typography as CustomTypography } from '../../components';
import AssignmentModalControls from './AssignmentModalControls';
import SearchResults from './SearchResults';
import { searchCustomer } from '../../services/inventory';
import { ICompSummaryBase } from '../../types';
import useSnackbarNotifications from '../../hooks/useSnackbarNotifications';
import { GLOBAL_USER } from '../../utils/constants';
import { LoadingDialogue } from '../../components/Spinners';
import AssignmentActionModal from './AssignmentActionModal';
// import { element } from 'prop-types';
import useHeight from '../../hooks/useHeight';
import { usePermissions } from '../../hooks/usePermissions';

interface SearchFormProps {
  onSubmit: (q: string) => void;
}

const SearchForm: React.FC<SearchFormProps> = ({ onSubmit }) => {
  const [value, setValue] = useState('');

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(value);
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        placeholder="Search by customer name or order id"
        fullWidth
        endIcon={
          <IconButton size="small" onClick={() => onSubmit(value)}>
            <Icon icon="ant-design:search-outlined" />
          </IconButton>
        }
        onChange={(e) => {
          const val = e.target.value;
          if ((val && val.length > 2) || val === '') {
            onSubmit(val);
          }
          setValue(val);
        }}
      />
      <Box
        width="98.5%"
        sx={{
          marginTop: '10px',
          borderColor: '#456BD9',
          borderStyle: 'solid',
          borderWidth: '1px',
          background: '#fff',
          borderRadius: '4px',
          padding: '7px 10px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Icon icon="ri:information-line" fontSize={16} color="#456BD9" />
        <Typography fontSize="12px" paddingLeft="10px" color="#456BD9">
          The order will not show if any of the following is true: the customer’s order country is not in the region
          this vehicle can be sold in, or the order is for a demo/pre-owned vehicle, or there is an active hold from the
          available cars page.
        </Typography>
      </Box>
    </form>
  );
};

interface SearchCustomerProps {
  onCustomerIdUpdate: (p: any) => void;
  toCompareVehicle: any;
}

const SearchCustomer: React.FC<SearchCustomerProps> = React.memo(({ onCustomerIdUpdate, toCompareVehicle }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<ISearchCustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<ISearchCustomer | null>(null);
  const [loading, setLoading] = useState(false);
  const elementDOM = useRef(null);
  const [boxHeight, setBoxHeight] = useState('');
  const { height, offsetTop, offsetHeight } = useHeight(elementDOM, true, true);

  const handleSearch = async () => {
    if (!searchTerm) {
      return;
    }
    try {
      setLoading(true);
      const res = await searchCustomer(searchTerm, toCompareVehicle.country, toCompareVehicle.listingCountry);
      setResults(res);
    } catch (error) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleSearch();
    onCustomerIdUpdate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const updateSelected = (v: any) => {
    setSelectedCustomer(v);
    if (v) {
      onCustomerIdUpdate({
        orderNumber: v.customerVehicle.orderNumber,
        customerId: v.customerVehicle.customerId,
        customerName: v.customerVehicle.customerName,
        countryCode: v.customerVehicle?.deliveryCountryCode,
      });
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Center>
          <CircularProgress />
        </Center>
      );
    }
    if (!searchTerm) {
      return (
        <Center>
          <Icon icon="ant-design:file-search-outlined" fontSize={28} color={theme.palette.grey[400]} />
          <Typography variant="body2" color={theme.palette.grey[400]}>
            You will see your search results here.
          </Typography>
        </Center>
      );
    }
    return !results.length ? (
      <Center>
        <Icon icon="healthicons:rdt-result-no-test" fontSize={28} color={theme.palette.grey[400]} />
        <Typography variant="body2" color={theme.palette.grey[400]}>
          Nothing to show
        </Typography>
      </Center>
    ) : (
      <Box sx={{ overflow: 'auto', height: `calc(90vh - ${boxHeight}px)` }}>
        <Stack spacing={1} width="99%">
          {results.map((result, i) => (
            <SearchResults
              // eslint-disable-next-line react/no-array-index-key
              key={result.customerVehicle._id}
              toCompareVehicle={toCompareVehicle}
              vehicle={result}
              onSelect={updateSelected}
              selected={selectedCustomer}
            />
          ))}
        </Stack>
      </Box>
    );
  };

  useEffect(() => {
    if (offsetTop && offsetHeight) {
      const tempboxHeight = `${offsetTop + offsetHeight}`;
      setBoxHeight(tempboxHeight);
    }
  }, [offsetTop, offsetHeight]);

  return (
    <>
      <Box ref={elementDOM}>
        <SearchForm
          onSubmit={(q) => {
            setSearchTerm(q);
            setSelectedCustomer(null);
          }}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
        }}
      >
        {renderBody()}
      </Box>
    </>
  );
});

interface Props {
  newToggle: any;
  toCompareVehicle: ICompSummaryBase & { [key: string]: string };
  closeModal: () => void;
}

const CustomerAssignment: React.FC<Props> = ({ newToggle, toCompareVehicle, closeModal }) => {
  const [decisionPending, setDecisionPending] = useState(false);
  const { showSuccessSnackbar, showErrorSnackbar, showInfoSnackbar } = useSnackbarNotifications();
  const [comment, setComment] = useState('');
  const [orderInfo, setOrderInfo] = useState<{
    customerId: string;
    orderNumber: string;
    customerName: string;
    countryCode: string;
  } | null>(null);
  const [creatingAssignment, setCreatingAssignment] = useState(false);
  const [page, setPage] = useState(0);
  const [nextDisable, setNextDisable] = useState(true);
  const { hasPermissions, getRole } = usePermissions();
  const [transactionErrors, setTransactionErrors] = useState(false);
  const [vehicleComponentChange, setVehicleComponentChange] = useState<IVehicleComponentChange>({
    wheelOptions: null,
    upgradeOptions: null,
  });
  const handleVehicleComponentChange = (type: string, updateComponent: IVehicleComponentChange) => {
    setVehicleComponentChange((prevState: IVehicleComponentChange): IVehicleComponentChange => {
      if (type === 'wheelOptions') {
        return {
          ...prevState,
          wheelOptions: updateComponent,
        };
      }
      if (type === 'upgradeOptions') {
        return {
          ...prevState,
          upgradeOptions: updateComponent,
        };
      }
      return {
        wheelOptions: null,
        upgradeOptions: null,
      };
    });
  };
  const handleOnCommentChange = useCallback((nc: string) => setComment(nc), []);

  const updateSelectedCustomer = useCallback((p: any) => {
    if (p) {
      setNextDisable(false);
    } else {
      setNextDisable(true);
    }
    setOrderInfo(p);
  }, []);

  const memToCompareVehicle = useMemo(() => {
    return toCompareVehicle;
  }, []);

  const handleCreateAssignment = async () => {
    try {
      setDecisionPending(true);
      setCreatingAssignment(true);
      const data = {
        assignmentType: 'customer_with_order',
        comments: comment,
        requestor: GLOBAL_USER,
        orderNumber: orderInfo?.orderNumber,
        customerId: orderInfo?.customerId || '',
        invBatchNumber: toCompareVehicle.batchNumber || '',
        vehicleComponentChange,
        directApproval: false,
      };
      const res = await createAssignment(data);
      // setDecisionPending(false);
      // showSuccessSnackbar({ message: 'The request has been successfully submitted.' });
      // closeModal();
      if (res.message === 'Success') {
        setDecisionPending(false);
        showSuccessSnackbar({ message: 'The request has been successfully submitted.' });
        closeModal();
      } else if (res?.sfdcResponse?.statusCode !== 200 || res?.vmsResponse?.statusCode !== 200) {
        setDecisionPending(false);
        setTransactionErrors(true);
      } else {
        setDecisionPending(false);
        showErrorSnackbar({ message: res.message });
        closeModal();
      }
    } catch (error) {
      showErrorSnackbar({ message: 'Error creating assignment' });
    } finally {
      setCreatingAssignment(false);
    }
  };

  const handleBack = () => {
    newToggle(true);
    // eslint-disable-next-line
    page > 0 ? setPage(page - 1) : closeModal();
  };

  const handleNextScreen = () => {
    if (orderInfo) {
      setPage(page + 1);
      newToggle(false);
      setNextDisable(true);
    }
  };

  // const handleAutoAssignment = () => {
  //   if (toCompareVehicle?.elegibleforAutoAssignment) {
  //     setAutoAssignment(true);
  //   }
  // };

  const handleDirectAssignment = async () => {
    try {
      setDecisionPending(true);
      setCreatingAssignment(true);
      const data = {
        assignmentType: 'customer_with_order',
        comments: comment || '',
        requestor: GLOBAL_USER,
        orderNumber: orderInfo?.orderNumber,
        customerId: orderInfo?.customerId || '',
        invBatchNumber: toCompareVehicle.batchNumber || '',
        directApproval: true,
        vehicleComponentChange,
      };
      const res = await createAssignment(data);
      if (res.message === 'Success') {
        setDecisionPending(false);
        showSuccessSnackbar({ message: 'The request has been successfully submitted.' });
        closeModal();
      } else if (
        (res?.sfdcResponse?.statusCode !== 200 || res?.vmsResponse?.statusCode !== 200) &&
        res.statusCode !== 200
      ) {
        setDecisionPending(false);
        setTransactionErrors(true);
        // if (
        //   res?.transactionErrors?.sfdcResponse?.statusCode === 200 &&
        //   res?.transactionErrors?.vmsResponse?.statusCode === 200
        // ) {
        //   const successMsg = `Salesforce : ${res?.transactionErrors?.sfdcResponse.data}
        //   SAP : ${res?.transactionErrors?.vmsResponse.data}`;
        //   showSuccessSnackbar({ message: successMsg });
        // } else {
        //   const errorMsg = `
        //     Salesforce : ${res?.transactionErrors?.sfdcResponse.data}
        //     SAP : ${res?.transactionErrors?.vmsResponse.data}`;
        //   showErrorSnackbar({ message: errorMsg });
        // }
      } else {
        setDecisionPending(false);
        showErrorSnackbar({ message: res.message });
        closeModal();
      }
    } catch (error) {
      showErrorSnackbar({ message: 'Error in Direct Assignment' });
    } finally {
      setCreatingAssignment(false);
    }
  };

  const conditionalComponent = () => {
    switch (page) {
      case 0:
        return <SearchCustomer toCompareVehicle={memToCompareVehicle} onCustomerIdUpdate={updateSelectedCustomer} />;
      case 1:
        return (
          <>
            <AssignmentActionModal
              componentChange={handleVehicleComponentChange}
              inventoryVehicle={toCompareVehicle}
              orderInfo={orderInfo}
              transactionErrors={transactionErrors}
            />
            <Divider sx={{ my: 1 }} />
            <Box component="footer">
              {toCompareVehicle?.elegibleforAutoAssignment && !hasPermissions(['PERFORM_REQUEST_ONLY']) && (
                <Box
                  sx={{
                    marginTop: '10px',
                    borderColor: '#456BD9',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    background: '#fff',
                    borderRadius: '4px',
                    padding: '5px 10px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Icon icon="ri:information-line" fontSize={14} color="#0365fc" />
                  <CustomTypography weight={400} size={12} color="#456BD9" marginLeft={1}>
                    This request is eligible for automatic assignment.
                  </CustomTypography>
                </Box>
              )}
              <Typography variant="caption" sx={{ lineHeight: '30px' }}>
                Request Reason
              </Typography>
              <TextField
                required
                placeholder="Enter Reason (Required)"
                value={comment}
                onChange={(e) => handleOnCommentChange(e.target.value)}
              />
            </Box>
          </>
        );
      default:
        return <SearchCustomer toCompareVehicle={memToCompareVehicle} onCustomerIdUpdate={updateSelectedCustomer} />;
    }
  };
  return (
    <>
      <LoadingDialogue open={decisionPending} />
      <Stack my={2} flex={1} spacing={1} display="flex">
        {conditionalComponent()}
      </Stack>
      <AssignmentModalControls
        onBack={handleBack}
        onSubmit={handleCreateAssignment}
        onDirectSubmit={handleDirectAssignment}
        visibilityFlag={page > 0}
        autoAssignmentElidgible={!!toCompareVehicle?.elegibleforAutoAssignment}
        showDirectSubmit
        submitDisabled={!orderInfo || creatingAssignment || comment === ''}
        submitDirectDisabled={!orderInfo || creatingAssignment}
        nextEnable={nextDisable}
        onNext={handleNextScreen}
        orderAvailability={!!orderInfo}
        autoAssignment={!toCompareVehicle?.elegibleforAutoAssignment}
      />
    </>
  );
};

export default CustomerAssignment;
